import React, { useEffect, useState } from 'react';
import { NextSeo } from 'next-seo';
import absoluteUrl from 'next-absolute-url';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next';
import { Button, useMediaQuery } from '@material-ui/core';
import Container from '@material-ui/core/Container';
import styled from 'styled-components';
import qs from 'query-string';

import ListingsApis from 'src/api/listings';

import { IListings, IParentCategory } from 'src/interface';
import Breadcrumb from 'src/components/Common/breadcrumb';
import useSearchListings from 'src/hooks/api/listings/useSearchListings';
import useGetAllCategories from 'src/hooks/api/categories/useGetAllCategories';
import EmptyBoxAnimation from 'src/components/Common/lottie/EmptyBox';
import List from '@material-ui/core/List';
import Link from 'next/link';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import RentableAd from 'src/components/Common/lottie/RentableAd';
import { BREAKPOINTS } from 'src/utils/constants';
import SearchPageBanner from 'src/components/Common/pageBanner';
import Grid from '@material-ui/core/Grid';
import { StyledGrid } from 'src/components/LandingPage/Components/ListingsSection';
import { StyledListingCard } from '../../components/Search';
// import { AdHorizontal } from '../index';
// import AdBanner from '../../components/Adsense';

type IStyledProps = {
  isMobile?: boolean;
};
const Content = styled.div<IStyledProps>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile && 'column-reverse') || 'row'};
  justify-content: flex-start;
`;
const ListingsContainer = styled('div')<IStyledProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: ${({ isMobile }) => (isMobile && '40px') || '0px 20px 30px'};
`;
const ViewMore = styled(Button)`
  margin: 20px 0 !important;
  padding: 10px 20px !important;
  align-self: center;
`;
const EmptySection = styled.div`
  display: flex;
  width: 100%;
  height: 50vh;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const SubCategoriesContainer = styled('div')`
  display: flex;
  flex-direction: column;
  flex-basis: 25%;
  min-width: 25%;
`;
const SubCategories = styled('div')``;
const AdContainer = styled.div<IStyledProps>`
  max-width: 100%;
  margin: ${({ isMobile }) => (isMobile && '50px 0') || '40px 0'};
`;

type Props = {
  canonicalUrl: string;
  listings: IListings;
  query: any;
};
const ParentCategory = ({ canonicalUrl, listings, query }: Props) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE);
  const router = useRouter();
  const [perPage, setPerPage] = useState(parseInt(listings.per_page, 10));
  const { Categories } = useGetAllCategories();
  const qry = {
    parent_cat: query.parentCategory,
    perPage,
  };
  const shouldFetch = perPage > 10 ? `/search?${qs.stringify(qry)}` : null;
  const { Listings, isLoading, canLoadMore } = useSearchListings(listings, shouldFetch, perPage);

  const [parentCategory, setParentCategory] = useState<IParentCategory | null>(null);
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

  useEffect(() => {
    if (Categories) {
      Categories.forEach((parentCat) => {
        if (parentCat.slug === query.parentCategory) {
          parentCat.children.forEach((child, index) => {
            if (child.slug === query.childCategory) {
              setSelectedIndex(index);
            }
          });
          setParentCategory(parentCat);
        }
      });
    }
  }, [Categories, query]);
  return (
    <>
      <NextSeo
        canonical={canonicalUrl}
        title={parentCategory?.name || 'Parent category'}
        description="Rentable has rental ads available in Pakistan of goods from cars, furniture, electronics to every other rentable service listings. Rent Now!"
        openGraph={{
          url: canonicalUrl,
          title: parentCategory?.name || 'Parent category',
          description:
            'Rentable has rental ads available in Pakistan of goods from cars, furniture, electronics to every other rentable service listings. Rent Now!',
          images: [
            {
              url: 'https://rentable.pk/assets/logos/green-text-logo.png',
              alt: 'Rentable Pakistan',
            },
          ],
        }}
      />
      <SearchPageBanner />
      {/* <AdHorizontal>
        <AdBanner slotId="4100436110" />
      </AdHorizontal> */}
      <Container maxWidth="lg">
        <Breadcrumb pathname={router.asPath} />
        <Content isMobile={isMobile}>
          <SubCategoriesContainer>
            <SubCategories>
              <List component="nav" aria-label="secondary mailbox folder">
                {parentCategory?.children.map((cat, index) => {
                  return (
                    <Link
                      key={index}
                      href="/[parentCategory]/[childCategory]"
                      as={`/${query.parentCategory}/${cat.slug}`}
                    >
                      <ListItem button selected={selectedIndex === index}>
                        <ListItemText primary={cat.name} />
                      </ListItem>
                    </Link>
                  );
                })}
              </List>
            </SubCategories>
            <AdContainer isMobile={isMobile}>
              <Link href="/post-listing" passHref>
                <a href="/post-listing">
                  <RentableAd width={300} height={300} />
                </a>
              </Link>
            </AdContainer>
          </SubCategoriesContainer>
          <ListingsContainer isMobile={isMobile}>
            <Grid container xs={12} spacing={2} direction="row">
              {Listings?.data.map((listing, key) => (
                <StyledGrid key={key} item xs={12} sm={4}>
                  <StyledListingCard isMobile={isMobile} key={listing.id} listingData={listing} />
                </StyledGrid>
              )) || (
                <EmptySection>
                  <EmptyBoxAnimation width={200} height={200} />
                  No Results Found
                </EmptySection>
              )}
            </Grid>
            {canLoadMore && (
              <ViewMore
                variant="contained"
                disabled={isLoading}
                color="primary"
                onClick={() => setPerPage(perPage + 10)}
              >
                Load more
              </ViewMore>
            )}
          </ListingsContainer>
        </Content>
      </Container>
      {/* <AdHorizontal>
        <AdBanner slotId="9529706829" />
      </AdHorizontal> */}
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { req, query } = ctx;
  const { origin } = absoluteUrl(req);
  // Get all listings
  const result = await ListingsApis.fetchListingByParentCategory({
    id: (query && query.parentCategory) || '',
  });
  return {
    props: {
      canonicalUrl: `${origin}/${query.parentCategory}/${query.childCategory}`,
      listings: result.data.listings,
      query,
    },
  };
};
export default ParentCategory;
